/* ==========================================================================
   GNJ global behaviours
   Namespaced under window.GNJ. No third-party dependencies.
   Covers: menu drawer, accordions, modal dialogs, toast status messages,
   cart-count sync and filter panels. Product page logic lives in
   sections/gnj-product.liquid and consumes GNJ.toast / GNJ.setCartCount.
   ========================================================================== */

(function () {
  'use strict';

  var GNJ = (window.GNJ = window.GNJ || {});

  function qs(sel, scope) { return (scope || document).querySelector(sel); }
  function qsa(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }

  /* ---------------------------------------------------------------- Toast */

  var toastEl = null;
  var toastTimer = null;

  function ensureToast() {
    if (toastEl && document.body.contains(toastEl)) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'gnj-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
    return toastEl;
  }

  GNJ.toast = function (message) {
    if (!message) return;
    var el = ensureToast();
    el.textContent = message;
    // Force a reflow so repeated messages re-announce and re-animate.
    void el.offsetWidth;
    el.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { el.classList.remove('is-visible'); }, 3600);
  };

  /* ----------------------------------------------------------- Cart count */

  GNJ.setCartCount = function (count) {
    var value = Number(count) || 0;
    qsa('[data-gnj-cart-count]').forEach(function (el) {
      el.textContent = value > 99 ? '99+' : String(value);
      el.hidden = value === 0;
      var link = el.closest('a, button');
      if (link && link.hasAttribute('data-gnj-cart-label')) {
        link.setAttribute('aria-label', link.getAttribute('data-gnj-cart-label') + (value ? ', ' + value + ' item' + (value === 1 ? '' : 's') : ', empty'));
      }
    });
  };

  GNJ.refreshCart = function () {
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart) GNJ.setCartCount(cart.item_count);
        return cart;
      })
      .catch(function () { return null; });
  };

  /* -------------------------------------------------- Scroll lock helpers */

  var lockCount = 0;
  var lockedScrollY = 0;

  function lockScroll() {
    if (lockCount === 0) {
      lockedScrollY = window.pageYOffset;
      document.body.style.top = -lockedScrollY + 'px';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.classList.add('gnj-scroll-locked');
    }
    lockCount += 1;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.classList.remove('gnj-scroll-locked');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, lockedScrollY);
    }
  }

  GNJ.lockScroll = lockScroll;
  GNJ.unlockScroll = unlockScroll;

  /* --------------------------------------------------------- Focus traps */

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]'
  ].map(function (sel) { return sel + ':not([tabindex="-1"])'; }).join(', ');

  function trapFocus(container, event) {
    var items = qsa(FOCUSABLE, container).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ------------------------------------------------------------- Drawers */

  function initDrawer() {
    var drawer = qs('[data-gnj-drawer]');
    var overlay = qs('[data-gnj-overlay]');
    var triggers = qsa('[data-gnj-drawer-open]');
    var closers = qsa('[data-gnj-drawer-close]');
    if (!drawer || !overlay || !triggers.length) return;

    var lastTrigger = null;
    var isOpen = false;

    drawer.hidden = false;
    overlay.hidden = false;

    function open(trigger) {
      if (isOpen) return;
      isOpen = true;
      lastTrigger = trigger || null;
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      lockScroll();
      var focusTarget = qs('[data-gnj-drawer-close]', drawer) || drawer;
      focusTarget.focus();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      unlockScroll();
      if (lastTrigger) lastTrigger.focus();
    }

    triggers.forEach(function (t) { t.addEventListener('click', function () { open(t); }); });
    closers.forEach(function (c) { c.addEventListener('click', close); });
    overlay.addEventListener('click', close);
    // Navigating away from the drawer should not leave the page scroll-locked.
    qsa('a[href]', drawer).forEach(function (link) { link.addEventListener('click', close); });

    document.addEventListener('keydown', function (event) {
      if (!isOpen) return;
      if (event.key === 'Escape') { close(); return; }
      if (event.key === 'Tab') trapFocus(drawer, event);
    });
  }

  /* ---------------------------------------------------------- Accordions */

  function initAccordions(scope) {
    qsa('[data-gnj-accordion-toggle]', scope).forEach(function (toggle) {
      if (toggle.dataset.gnjBound === 'true') return;
      toggle.dataset.gnjBound = 'true';
      toggle.addEventListener('click', function () {
        var panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (!panel) return;
        var isOpen = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isOpen));
        panel.hidden = isOpen;
      });
    });
  }

  GNJ.initAccordions = initAccordions;

  /* -------------------------------------------------------------- Modals */

  function initModals() {
    qsa('[data-gnj-modal]').forEach(function (modal) {
      var openers = qsa('[data-gnj-modal-open="' + modal.id + '"]');
      var closers = qsa('[data-gnj-modal-close]', modal);
      var lastTrigger = null;
      if (!openers.length) return;

      function open(trigger) {
        lastTrigger = trigger || null;
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        lockScroll();
        var target = qs(FOCUSABLE, modal);
        if (target) target.focus();
      }

      function close() {
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        unlockScroll();
        if (lastTrigger) lastTrigger.focus();
      }

      openers.forEach(function (o) { o.addEventListener('click', function () { open(o); }); });
      closers.forEach(function (c) { c.addEventListener('click', close); });

      modal.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') { event.stopPropagation(); close(); return; }
        if (event.key === 'Tab') trapFocus(modal, event);
      });
    });
  }

  /* ------------------------------------------------------- Filter panels */

  function initDisclosures() {
    qsa('[data-gnj-disclosure]').forEach(function (trigger) {
      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;
      trigger.addEventListener('click', function () {
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!isOpen));
        panel.classList.toggle('is-open', !isOpen);
      });
    });
  }

  /* --------------------------------------------------------- Cart page */

  function initCartPage() {
    var cartPage = qs('.gnj-cart-page');
    if (!cartPage || cartPage.dataset.gnjCartBound === 'true') return;
    cartPage.dataset.gnjCartBound = 'true';

    var isUpdating = false;
    var deliveryGoal = Number(cartPage.dataset.deliveryGoal) || 40000;
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AED';

    function money(cents) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AE', {
          style: 'currency',
          currency: currency,
          currencyDisplay: 'code',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format((Number(cents) || 0) / 100).replace(/\u00a0/g, ' ');
      } catch (error) {
        return currency + ' ' + ((Number(cents) || 0) / 100).toFixed(2);
      }
    }

    function announce(message) {
      var status = qs('[data-gnj-cart-status]', cartPage);
      if (status) status.textContent = message;
    }

    function setBusy(value) {
      isUpdating = value;
      cartPage.classList.toggle('is-updating', value);
      cartPage.setAttribute('aria-busy', String(value));
      qsa('.js-qty__adjust, .js-qty__num', cartPage).forEach(function (control) {
        control.disabled = value;
      });
    }

    function refreshProgress(totalPrice) {
      var progress = qs('.gnj-cart-progress', cartPage);
      if (!progress) return;
      var remaining = Math.max(0, deliveryGoal - totalPrice);
      var percent = Math.min(100, Math.round((totalPrice / deliveryGoal) * 100));
      var strong = qs('.gnj-cart-progress__copy strong', progress);
      var note = qs('.gnj-cart-progress__copy span', progress);
      var fill = qs('.gnj-cart-progress__track span', progress);

      progress.classList.toggle('is-complete', remaining === 0);
      var threshold = currency + ' ' + String(deliveryGoal / 100);
      progress.setAttribute('aria-valuemax', String(deliveryGoal / 100));
      progress.setAttribute('aria-valuenow', String(Math.min(deliveryGoal, totalPrice) / 100));
      var thresholdLabel = qs('[data-gnj-delivery-threshold]', cartPage);
      if (thresholdLabel) thresholdLabel.textContent = threshold + '+';
      var announcement = qs('.gnj-announcement > span, .gnj-announcement > a');
      if (announcement) announcement.textContent = cartPage.dataset.deliveryAnnouncement.replace('[threshold]', threshold);
      if (fill) fill.style.width = percent + '%';
      if (remaining === 0) {
        if (strong) strong.textContent = 'Free worldwide shipping is included.';
        if (note) note.textContent = 'Your bag qualifies automatically.';
      } else {
        if (strong) strong.textContent = cartPage.dataset.deliveryHeading.replace('[threshold]', threshold);
        if (note) note.textContent = 'Add ' + money(remaining) + ' more to unlock it.';
      }
    }

    function refreshMarkup(cart) {
      if (!cart.item_count) {
        window.location.reload();
        return Promise.resolve();
      }

      var cartAjaxUrl = (window.theme && theme.routes && theme.routes.cartPage ? theme.routes.cartPage : '/cart') + '?view=ajax&t=' + Date.now();
      var previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
      if (previewThemeId) cartAjaxUrl += '&preview_theme_id=' + encodeURIComponent(previewThemeId);
      return fetch(cartAjaxUrl, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('Unable to refresh cart');
          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var freshItems = qs('.cart__items', parsed);
          var itemTarget = qs('[data-products]', cartPage);
          if (!freshItems || !itemTarget) throw new Error('Cart markup was incomplete');
          itemTarget.innerHTML = freshItems.innerHTML;

          var discountTarget = qs('[data-discounts]', cartPage);
          var freshDiscounts = qs('.cart__discounts', parsed);
          if (discountTarget) discountTarget.innerHTML = freshDiscounts ? freshDiscounts.innerHTML : '';

          var subtotal = qs('[data-subtotal]', cartPage);
          if (subtotal) subtotal.textContent = money(cart.total_price);

          var savings = qs('[data-savings]', cartPage);
          var totalSavings = Number(cart.total_discount || 0);
          if (savings) {
            savings.classList.toggle('hide', totalSavings <= 0);
            if (totalSavings > 0) {
              var savingText = window.theme && theme.strings && theme.strings.cartSavings
                ? theme.strings.cartSavings.replace('[savings]', money(totalSavings))
                : 'You saved ' + money(totalSavings);
              savings.innerHTML = savingText;
            }
          }

          deliveryGoal = Number(freshItems.dataset.gnjDeliveryGoal) || 40000;
          cartPage.dataset.deliveryGoal = String(deliveryGoal);
          refreshProgress(Number(cart.total_price || 0));
          if (GNJ.setCartCount) GNJ.setCartCount(cart.item_count);
          announce('Bag updated. ' + cart.item_count + ' item' + (cart.item_count === 1 ? '' : 's') + ', subtotal ' + money(cart.total_price) + '.');
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart } }));
        });
    }

    function changeQuantity(input, quantity) {
      if (isUpdating || !input || !input.dataset.id) return;
      var previous = Math.max(0, parseInt(input.value, 10) || 0);
      var next = Math.max(0, parseInt(quantity, 10) || 0);
      input.value = next;
      setBusy(true);

      fetch(window.theme && theme.routes && theme.routes.cartChange ? theme.routes.cartChange : '/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: input.dataset.id, quantity: next })
      })
        .then(function (response) {
          if (!response.ok) return response.json().then(function (body) { throw new Error(body.description || 'Unable to update bag'); });
          return response.json();
        })
        .then(refreshMarkup)
        .catch(function (error) {
          input.value = previous;
          announce(error.message || 'The bag could not be updated.');
          if (GNJ.toast) GNJ.toast(error.message || 'The bag could not be updated. Please try again.');
        })
        .finally(function () { setBusy(false); });
    }

    cartPage.addEventListener('click', function (event) {
      var button = event.target.closest('.js-qty__adjust');
      if (!button || !cartPage.contains(button)) return;
      event.preventDefault();
      var wrapper = button.closest('.js-qty__wrapper');
      var input = wrapper && qs('.js-qty__num', wrapper);
      if (!input) return;
      var current = parseInt(input.value, 10) || 0;
      changeQuantity(input, current + (button.classList.contains('js-qty__adjust--plus') ? 1 : -1));
    });

    cartPage.addEventListener('change', function (event) {
      if (!event.target.matches('.js-qty__num')) return;
      changeQuantity(event.target, event.target.value);
    });

    cartPage.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' || !event.target.matches('.js-qty__num')) return;
      event.preventDefault();
      changeQuantity(event.target, event.target.value);
    });
  }

  /* ----------------------------------------------------------- Bootstrap */

  function init() {
    initDrawer();
    initAccordions(document);
    initModals();
    initDisclosures();
    initCartPage();
    GNJ.refreshCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
